import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Subject } from 'rxjs';
import { TestGridComponent } from './components/test-grid/test-grid.component';
import { TestCategory, TestControl } from './models/test-control.interface';
import { TestRegistryService } from './services/test-registry.service';

/**
 * Refactored test bench component using modular architecture
 */
@Component({
  selector: 'app-test-bench-refactored',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatToolbarModule,
    MatDividerModule,
    MatChipsModule,
    TestGridComponent
  ],
  template: `
    <div class="test-bench-container">
      <!-- Header -->
      <mat-toolbar color="primary" class="test-bench-header">
        <mat-icon>science</mat-icon>
        <span>Development Test Bench</span>
        <span class="spacer"></span>
        <mat-chip-set>
          <mat-chip [highlighted]="true">
            {{getTotalTestsCount()}} tests
          </mat-chip>
          <mat-chip [highlighted]="true">
            {{categories.length}} categories
          </mat-chip>
        </mat-chip-set>
      </mat-toolbar>

      <!-- Content -->
      <div class="test-bench-content">
        <!-- Empty State -->
        <div *ngIf="categories.length === 0" class="empty-state">
          <mat-icon>science</mat-icon>
          <h2>No Test Categories Registered</h2>
          <p>Test modules need to be imported to register their tests.</p>
          <button mat-raised-button color="primary" (click)="refreshCategories()">
            <mat-icon>refresh</mat-icon>
            Refresh
          </button>
        </div>

        <!-- Test Categories -->
        <div *ngIf="categories.length > 0" class="categories-container">
          <app-test-grid 
            *ngFor="let category of categories; trackBy: trackByCategoryId" 
            [category]="category"
            [tests]="getTestsForCategory(category.id)">
          </app-test-grid>
        </div>

        <!-- Quick Actions -->
        <div class="quick-actions">
          <mat-divider></mat-divider>
          <div class="actions-row">
            <button 
              mat-stroked-button 
              (click)="refreshCategories()"
              matTooltip="Refresh test categories">
              <mat-icon>refresh</mat-icon>
              Refresh
            </button>
            <button 
              mat-stroked-button 
              (click)="clearConsole()"
              matTooltip="Clear console output">
              <mat-icon>clear_all</mat-icon>
              Clear Console
            </button>
            <button 
              mat-stroked-button 
              (click)="exportTestResults()"
              matTooltip="Export test results">
              <mat-icon>download</mat-icon>
              Export
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .test-bench-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
    }

    .test-bench-header {
      flex-shrink: 0;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      margin: -1rem -1rem 0 -1rem;
    }

    .test-bench-header .spacer {
      flex: 1;
    }

    .test-bench-header mat-chip-set {
      gap: 8px;
    }

    .test-bench-content {
      flex: 1;
      overflow-y: auto;
      padding: 24px;
      min-height: 0;
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 50vh;
      text-align: center;
      color: #666;
    }

    .empty-state mat-icon {
      font-size: 64px;
      width: 64px;
      height: 64px;
      margin-bottom: 16px;
    }

    .empty-state h2 {
      margin: 0 0 8px 0;
      font-weight: 400;
    }

    .empty-state p {
      margin: 0 0 24px 0;
      font-size: 14px;
    }

    .categories-container {
      max-width: 1200px;
      margin: 0 auto;
    }

    .quick-actions {
      margin-top: 32px;
      padding-top: 24px;
    }

    .actions-row {
      display: flex;
      gap: 12px;
      justify-content: center;
      margin-top: 16px;
    }

    /* Responsive design */
    @media (max-width: 767px) {
      .test-bench-header {
        margin: -1rem 0 0 0;
      }
      
      .test-bench-content {
        padding: 16px;
      }
      
      .test-bench-header mat-chip-set {
        display: none;
      }
    }

    /* Box sizing for all elements */
    * {
      box-sizing: border-box;
    }
  `]
})
export class TestBenchRefactoredComponent implements OnInit, OnDestroy {
  categories: TestCategory[] = [];
  private destroy$ = new Subject<void>();

  constructor(private testRegistry: TestRegistryService) { }

  ngOnInit(): void {
    this.loadCategories();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load all registered categories
   */
  private loadCategories(): void {
    try {
      this.categories = this.testRegistry.getAllCategories();
      console.log('📂 Loaded categories:', this.categories.length, 'categories');

      // Log category details
      this.categories.forEach(cat => {
        const testCount = this.testRegistry.getTestsCountByCategory(cat.id);
        console.log(`  📁 ${cat.title}: ${testCount} tests`);
      });

    } catch (error) {
      console.error('❌ Error loading categories:', error);
      this.categories = [];
    }
  }

  /**
   * Get tests for a specific category
   */
  getTestsForCategory(categoryId: string): TestControl[] {
    try {
      return this.testRegistry.getTestsByCategory(categoryId);
    } catch (error) {
      console.error(`❌ Error getting tests for category '${categoryId}':`, error);
      return [];
    }
  }

  /**
   * Track by function for efficient rendering
   */
  trackByCategoryId(index: number, category: TestCategory): string {
    return category.id;
  }

  /**
   * Get total count of all tests
   */
  getTotalTestsCount(): number {
    try {
      return this.testRegistry.getAllTests().length;
    } catch (error) {
      console.error('❌ Error getting total tests count:', error);
      return 0;
    }
  }

  /**
   * Refresh categories (useful for development)
   */
  refreshCategories(): void {
    try {
      this.loadCategories();
      console.log('🔄 Categories refreshed');
    } catch (error) {
      console.error('❌ Error refreshing categories:', error);
    }
  }

  /**
   * Clear console output
   */
  clearConsole(): void {
    console.clear();
    console.log('Console cleared');
  }

  /**
   * Export test results (placeholder)
   */
  exportTestResults(): void {
    try {
      const testSummary = {
        totalTests: this.getTotalTestsCount(),
        categories: this.categories.map(cat => ({
          id: cat.id,
          title: cat.title,
          testCount: this.getTestsForCategory(cat.id).length
        })),
        timestamp: new Date().toISOString()
      };

      console.log('📊 Test bench summary:', testSummary);

      // Download as JSON file
      const blob = new Blob([JSON.stringify(testSummary, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `test-bench-summary-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);

      console.log('✅ Test bench summary exported successfully');

    } catch (error) {
      console.error('❌ Error exporting test results:', error);
    }
  }
}